import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CreditCard, Clock, Calendar, CheckCircle, AlertCircle, BarChart, Settings, Download } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../../firebaseConfig';
import Navbar from '../../components/Navbar';
import { useAuth } from '../../contexts/AuthContext';
import { useError } from '../../contexts/ErrorContext';
import Button from '../../components/ui/Button';

const SubscriptionPage = () => {
  const { showError } = useError();
  const { user } = useAuth();
  const [subscriptionData, setSubscriptionData] = useState({
    planName: 'Carregando...',
    price: 0,
    priceId: '',
    startDate: new Date().toISOString(),
    status: '',
    currentPeriodEnd: null,
    cancelAtPeriodEnd: false
  });
  const [userData, setUserData] = useState({
    nome: '',
    sobrenome: '',
    email: '',
    createdAt: '',
  });
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user) {
      fetchSubscriptionData();
    }
  }, [user]);

  const fetchSubscriptionData = async () => {
    try {
      const getSubscriptionData = httpsCallable(functions, 'get_subscription');
      // httpsCallable vai fazer uma chamada POST internamente
      const result = await getSubscriptionData();
      
      const { subscription, userData, paymentHistory } = result.data;
      
      setSubscriptionData(subscription);
      setUserData(userData);
      setPaymentHistory(paymentHistory);
    } catch (err) {
      showError(err);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Tem certeza que deseja cancelar sua assinatura?')) {
      return;
    }
  
    setLoading(true);
    setError(null);
  
    try {
      const cancelSubscriptionFn = httpsCallable(functions, 'cancel_subscription');
      await cancelSubscriptionFn();
      // Sync cancellation status to Firestore
      const syncFn = httpsCallable(functions, 'sync_subscription');
      await syncFn();
      await auth.currentUser.getIdToken(true);
      await fetchSubscriptionData();
      showError('Sua assinatura será cancelada ao final do período atual.');
    } catch (err) {
      showError(err);
    } finally {
      setLoading(false);
    }
  };

  const getPlanFeatures = (planName) => {
    if (planName === 'Plano Gratuito') {
      return [
        'Mapa Astral Básico',
        'Análise Limitada',
        'Recursos Básicos',
      ];
    }
    return [
      'Mapas Astrais Ilimitados',
      'Análise de Compatibilidade',
      'Previsões Personalizadas',
      'Relatórios Detalhados',
      'Suporte Premium',
    ];
  };

  const isSubscriptionActive = () => {
    return subscriptionData?.status === 'active' && !subscriptionData?.cancelAtPeriodEnd;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg p-8 border border-purple-100 relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 -translate-y-8 translate-x-8 w-64 h-64 bg-gradient-to-br from-purple-200 to-pink-200 rounded-full filter blur-3xl opacity-30 animate-pulse"></div>
          <div className="absolute bottom-0 left-0 translate-y-8 -translate-x-8 w-64 h-64 bg-gradient-to-br from-purple-200 to-pink-200 rounded-full filter blur-3xl opacity-30 animate-pulse"></div>

          <div className="relative">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Seu Plano Atual
                </h1>
                <p className="text-gray-500">{userData.nome} {userData.sobrenome}</p>
              </div>
              <div className="bg-purple-50 px-4 py-2 rounded-lg">
                <span className="text-purple-600 font-medium">
                  {subscriptionData.planName}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-xl">
                <div className="flex items-center gap-3 mb-2">
                  <CreditCard className="text-purple-600" size={20} />
                  <span className="text-gray-700 font-medium">Valor Mensal</span>
                </div>
                <p className="text-2xl font-bold text-purple-600">
                  {subscriptionData.price === 0 ? 'Grátis' : `R$ ${subscriptionData.price.toFixed(2)}`}
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-xl">
                <div className="flex items-center gap-3 mb-2">
                  <Clock className="text-purple-600" size={20} />
                  <span className="text-gray-700 font-medium">Status</span>
                </div>
                <p className="text-2xl font-bold text-green-600">Ativo</p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-xl">
                <div className="flex items-center gap-3 mb-2">
                  <Calendar className="text-purple-600" size={20} />
                  <span className="text-gray-700 font-medium">Início</span>
                </div>
                <p className="text-2xl font-bold text-purple-600">
                  {new Date(subscriptionData.startDate).toLocaleDateString()}
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-xl">
                <div className="flex items-center gap-3 mb-2">
                  <BarChart className="text-purple-600" size={20} />
                  <span className="text-gray-700 font-medium">Email</span>
                </div>
                <p className="text-sm font-bold text-purple-600">{userData.email}</p>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="md:col-span-2 bg-white rounded-2xl shadow-lg p-6 border border-purple-100"
          >
            <h2 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-6">
              Recursos do Seu Plano
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {getPlanFeatures(subscriptionData.planName).map((feature, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                  <CheckCircle className="text-purple-600" size={20} />
                  <span className="text-gray-700">{feature}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100"
          >
            <h2 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-6">
              Ações Rápidas
            </h2>
            <div className="space-y-4">
              {isSubscriptionActive() && !subscriptionData.cancelAtPeriodEnd && (
                <Button
                  variant="danger"
                  onClick={handleCancelSubscription}
                  fullWidth
                >
                  Cancelar Assinatura
                </Button>
              )}
              {subscriptionData.cancelAtPeriodEnd && (
                <div className="w-full flex items-center gap-3 px-4 py-3 bg-yellow-50 rounded-lg text-yellow-600">
                  <AlertCircle size={20} />
                  <span>Assinatura será cancelada ao final do período</span>
                </div>
              )}
              {error && (
                <div className="w-full flex items-center gap-3 px-4 py-3 bg-red-50 rounded-lg text-red-600">
                  <AlertCircle size={20} />
                  <span>{error}</span>
                </div>
              )}
              <button className="w-full flex items-center gap-3 px-4 py-3 bg-purple-50 rounded-lg text-purple-600 hover:bg-purple-100 transition-colors">
                <Download size={20} />
                <span>Baixar Faturas</span>
              </button>
            </div>
          </motion.div>
        </div>

        {subscriptionData.planName !== 'Plano Gratuito' && paymentHistory.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100"
          >
            <h2 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-6">
              Histórico de Pagamentos
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-purple-100">
                    <th className="text-left py-3 px-4 text-gray-600">Data</th>
                    <th className="text-left py-3 px-4 text-gray-600">ID</th>
                    <th className="text-left py-3 px-4 text-gray-600">Valor</th>
                    <th className="text-left py-3 px-4 text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentHistory.map((payment, index) => (
                    <tr key={index} className="border-b border-purple-50">
                      <td className="py-3 px-4 text-gray-700">{payment.date}</td>
                      <td className="py-3 px-4 text-gray-700">{payment.id}</td>
                      <td className="py-3 px-4 text-gray-700">{payment.amount}</td>
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center gap-2 px-3 py-1 bg-green-50 text-green-600 rounded-full">
                          <CheckCircle size={16} />
                          Confirmado
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionPage;
